// Admin Products Page

import {
	useState,
	useEffect,
	useCallback,
	useDispatch,
	useSelector,
	useRProductsMutation,
	useRProductCategoriesMutation,
	useAdminListTrashedProductsMutation,
	useAdminCreateProductMutation,
	useAdminUpdateProductMutation,
	useAdminUpdateProductImageMutation,
	useAdminTrashProductMutation,
	useAdminRestoreProductMutation,
} from "custom_exports/all_hooks";
import {
	Box,
	Typography,
	Button,
	TextField,
	Stack,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Chip,
	Card,
	InputAdornment,
	CircularProgress,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
} from "custom_exports/material_ui_items";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import ImageIcon from "@mui/icons-material/Image";
import Image from "components/custom/image/Image";
import {
	adminProductActions,
	productActions,
	productCategoryActions,
	getAdminProductItemsAsList,
	getProductItemsAsList,
	getProductCategoryItemsAsList,
} from "services/redux/reducers/slice";
import { callApi } from "common/api_handlers/api_call_manager";
import K from "common/keywords";
import Swal from "sweetalert2";
import { THEME } from "app/settings/settings";

function AdminProductsPage() {
	//--------------------------------- variables ---------------------------------//
	const dispatch = useDispatch();
	const { setAdminProducts, addAdminProduct, updateAdminProduct, removeAdminProduct } =
		adminProductActions;
	const { setProduct } = productActions;
	const { setProductCategory } = productCategoryActions;

	const adminProductsAsList = useSelector(getAdminProductItemsAsList);
	const liveProductsAsList = useSelector(getProductItemsAsList);
	const categoriesAsList = useSelector(getProductCategoryItemsAsList);

	const [searchQuery, setSearchQuery] = useState("");
	const [categoryFilter, setCategoryFilter] = useState("");
	const [viewMode, setViewMode] = useState("live"); // "live" or "trashed"
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editingProduct, setEditingProduct] = useState(null);
	const [formData, setFormData] = useState({ title: "", price: "", category_ids: [] });
	const [imageFile, setImageFile] = useState(null);
	const [imagePreview, setImagePreview] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	const [RProductsApiCaller] = useRProductsMutation();
	const [RProductCategoriesApiCaller] = useRProductCategoriesMutation();
	const [adminListTrashedApiCaller] = useAdminListTrashedProductsMutation();
	const [adminCreateApiCaller] = useAdminCreateProductMutation();
	const [adminUpdateApiCaller] = useAdminUpdateProductMutation();
	const [adminUpdateImageApiCaller] = useAdminUpdateProductImageMutation();
	const [adminTrashApiCaller] = useAdminTrashProductMutation();
	const [adminRestoreApiCaller] = useAdminRestoreProductMutation();

	//--------------------------------- functions ---------------------------------//
	const onApiResponse = ({ apiCallerId, ...response }) => {
		const { data } = response;
		if (apiCallerId === K.adminListTrashedProducts) {
			dispatch(setAdminProducts(data.products.items));
		} else if (apiCallerId === K.adminCreateProduct) {
			dispatch(addAdminProduct(data.products.item));
			refreshLiveProducts();
			Swal.fire({ text: "Product created successfully", icon: "success", confirmButtonColor: THEME.app.colors.primary.main });
			handleCloseDialog();
		} else if (apiCallerId === K.adminUpdateProduct) {
			dispatch(updateAdminProduct(data.products.item));
			refreshLiveProducts();
			Swal.fire({ text: "Product updated successfully", icon: "success", confirmButtonColor: THEME.app.colors.primary.main });
			handleCloseDialog();
		} else if (apiCallerId === K.adminUpdateProductImage) {
			dispatch(updateAdminProduct(data.products.item));
			refreshLiveProducts();
		} else if (apiCallerId === K.adminTrashProduct) {
			refreshLiveProducts();
			Swal.fire({ text: "Product deleted successfully", icon: "success", confirmButtonColor: THEME.app.colors.primary.main });
		} else if (apiCallerId === K.adminRestoreProduct) {
			const restoredId = data?.products?.item?.id;
			if (restoredId) dispatch(removeAdminProduct(restoredId));
			refreshLiveProducts();
			Swal.fire({ text: "Product restored successfully", icon: "success", confirmButtonColor: THEME.app.colors.primary.main });
		}
		setIsLoading(false);
	};

	const onApiError = ({ apiCallerId, ...error }) => {
		console.log(error);
		Swal.fire({ text: error.errorMessage || "Something went wrong", icon: "error", confirmButtonColor: THEME.app.colors.red.main });
		setIsLoading(false);
	};

	const apiActions = { onApiResponse, onApiError };

	const refreshLiveProducts = () => {
		callApi({
			apiCallerId: K.RProducts,
			apiCaller: RProductsApiCaller,
			apiInputParams: {},
			apiActions: {
				onApiResponse: ({ data }) => dispatch(setProduct(data.products.items)),
				onApiError: (err) => console.log(err),
			},
		});
	};

	const loadTrashedProducts = useCallback(() => {
		setIsLoading(true);
		callApi({
			apiCallerId: K.adminListTrashedProducts,
			apiCaller: adminListTrashedApiCaller,
			apiInputParams: {},
			apiActions,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// ---- handlers ---- //
	const handleOpenCreateDialog = () => {
		setEditingProduct(null);
		setFormData({ title: "", price: "", category_ids: [] });
		setImageFile(null);
		setImagePreview(null);
		setDialogOpen(true);
	};

	const handleOpenEditDialog = (product) => {
		setEditingProduct(product);
		setFormData({
			title: product.title,
			price: product.price,
			category_ids: product.categories || [],
		});
		setImageFile(null);
		setImagePreview(product.image || null);
		setDialogOpen(true);
	};

	const handleCloseDialog = () => {
		setDialogOpen(false);
		setEditingProduct(null);
		setFormData({ title: "", price: "", category_ids: [] });
		setImageFile(null);
		setImagePreview(null);
	};

	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setImageFile(file);
			setImagePreview(URL.createObjectURL(file));
		}
	};

	const handleSubmit = async () => {
		if (!formData.title.trim()) {
			Swal.fire({ text: "Title is required", icon: "warning", confirmButtonColor: THEME.app.colors.primary.main });
			return;
		}
		if (!formData.price) {
			Swal.fire({ text: "Price is required", icon: "warning", confirmButtonColor: THEME.app.colors.primary.main });
			return;
		}

		setIsLoading(true);

		if (editingProduct) {
			// Update existing product
			const updateParams = { id: editingProduct.id };
			if (formData.title !== editingProduct.title) updateParams.title = formData.title;
			if (formData.price !== editingProduct.price) updateParams.price = formData.price;
			updateParams.category_ids = formData.category_ids;

			const updateResult = await callApi({
				apiCallerId: K.adminUpdateProduct,
				apiCaller: adminUpdateApiCaller,
				apiInputParams: updateParams,
				apiActions,
			});

			// Update image separately after product update completes
			if (imageFile && updateResult?.response) {
				const imageFormData = new FormData();
				imageFormData.append("id", editingProduct.id);
				imageFormData.append("image", imageFile);
				callApi({
					apiCallerId: K.adminUpdateProductImage,
					apiCaller: adminUpdateImageApiCaller,
					apiInputParams: imageFormData,
					apiActions,
				});
			}
		} else {
			// Create new product
			if (imageFile) {
				const createFormData = new FormData();
				createFormData.append("title", formData.title);
				createFormData.append("price", formData.price);
				if (formData.category_ids.length > 0) {
					formData.category_ids.forEach((id) => createFormData.append("category_ids", id));
				}
				createFormData.append("image", imageFile);
				callApi({
					apiCallerId: K.adminCreateProduct,
					apiCaller: adminCreateApiCaller,
					apiInputParams: createFormData,
					apiActions,
				});
			} else {
				callApi({
					apiCallerId: K.adminCreateProduct,
					apiCaller: adminCreateApiCaller,
					apiInputParams: {
						title: formData.title,
						price: formData.price,
						category_ids: formData.category_ids,
					},
					apiActions,
				});
			}
		}
	};

	const handleTrashProduct = (product) => {
		Swal.fire({
			title: "Delete Product?",
			text: `Are you sure you want to delete "${product.title}"?`,
			icon: "warning",
			showCancelButton: true,
			confirmButtonColor: THEME.app.colors.red.main,
			cancelButtonColor: THEME.app.colors.grey.main,
			confirmButtonText: "Yes, delete it",
		}).then((result) => {
			if (result.isConfirmed) {
				setIsLoading(true);
				callApi({
					apiCallerId: K.adminTrashProduct,
					apiCaller: adminTrashApiCaller,
					apiInputParams: { id: product.id },
					apiActions,
				});
			}
		});
	};

	const handleRestoreProduct = (product) => {
		setIsLoading(true);
		callApi({
			apiCallerId: K.adminRestoreProduct,
			apiCaller: adminRestoreApiCaller,
			apiInputParams: { id: product.id },
			apiActions,
		});
	};

	// ---- filtering ---- //
	const getDisplayProducts = useCallback(() => {
		let filtered = viewMode === "trashed" ? adminProductsAsList : liveProductsAsList;
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			filtered = filtered.filter((p) => p.title.toLowerCase().includes(query));
		}
		if (categoryFilter) {
			filtered = filtered.filter((p) => p.categories && p.categories.includes(categoryFilter));
		}
		return filtered;
	}, [viewMode, adminProductsAsList, liveProductsAsList, searchQuery, categoryFilter]);

	// ---- useEffects ---- //
	useEffect(() => {
		if (liveProductsAsList.length <= 0) {
			refreshLiveProducts();
		}
		if (categoriesAsList.length <= 0) {
			callApi({
				apiCallerId: K.RProductCategories,
				apiCaller: RProductCategoriesApiCaller,
				apiInputParams: {},
				apiActions: {
					onApiResponse: ({ data }) => dispatch(setProductCategory(data.product_categories.items)),
					onApiError: (err) => console.log(err),
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (viewMode === "trashed") {
			loadTrashedProducts();
		}
	}, [viewMode, loadTrashedProducts]);

	//--------------------------------- contents ----------------------------------//
	const displayProducts = getDisplayProducts();

	const content = (
		<Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", p: { xs: 2, sm: 3 } }}>
			{/* Header */}
			<Box sx={{ bgcolor: "white", borderRadius: "24px", boxShadow: "0 2px 12px rgba(0,0,0,0.08)", p: 2, px: { xs: 2, sm: 3 }, mb: 3 }}>
				<Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between">
					<Stack direction="row" spacing={2} alignItems="center">
						<TextField
							size="small"
							sx={{
								width: { xs: "100%", sm: "300px" },
								"& .MuiOutlinedInput-root": {
									borderRadius: "20px", bgcolor: "#f1f5f9",
									"& fieldset": { border: "none" },
									"&.Mui-focused fieldset": { border: "2px solid #6bc637" },
								},
							}}
							placeholder="Search products..."
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "grey.500" }} /></InputAdornment> }}
						/>
						<FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 180 } }}>
							<InputLabel>Category</InputLabel>
							<Select
								value={categoryFilter}
								label="Category"
								onChange={(e) => setCategoryFilter(e.target.value)}
								sx={{
									borderRadius: "20px", bgcolor: "#f1f5f9",
									"& .MuiOutlinedInput-notchedOutline": { border: "none" },
									"&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "2px solid #6bc637" },
								}}
							>
								<MenuItem value="">All Categories</MenuItem>
								{categoriesAsList.map((cat) => (
									<MenuItem key={cat.id} value={cat.id} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
										{cat.title}
										{!cat.is_physical && (
											<Chip label="Service" size="small" color="warning" sx={{ fontSize: "0.65rem", height: 18, ml: 1 }} />
										)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<Stack direction="row" spacing={1}>
							<Chip
								label="Live"
								onClick={() => setViewMode("live")}
								color={viewMode === "live" ? "success" : "default"}
								variant={viewMode === "live" ? "filled" : "outlined"}
							/>
							<Chip
								label="Trashed"
								onClick={() => setViewMode("trashed")}
								color={viewMode === "trashed" ? "error" : "default"}
								variant={viewMode === "trashed" ? "filled" : "outlined"}
							/>
						</Stack>
					</Stack>
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={handleOpenCreateDialog}
						sx={{ borderRadius: "20px", bgcolor: "#6bc637", "&:hover": { bgcolor: "#5ab02e" }, textTransform: "none", fontWeight: 600 }}
					>
						Add Product
					</Button>
				</Stack>
			</Box>

			{/* Products Grid */}
			{isLoading && viewMode === "trashed" ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress sx={{ color: "#6bc637" }} /></Box>
			) : displayProducts.length === 0 ? (
				<Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
					<Typography color="text.secondary">{viewMode === "trashed" ? "No trashed products" : "No products found"}</Typography>
				</Box>
			) : (
				<Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 1, sm: 3 }, justifyContent: "space-evenly", p: 1 }}>
					{displayProducts.map((product) => (
						<Card
							key={product.id}
							sx={{
								borderRadius: 2,
								width: { xs: "calc(50% - 8px)", sm: 200 },
								position: "relative",
								"&:hover .admin-actions": { opacity: 1 },
							}}
						>
							{/* Image */}
							<Box sx={{ position: "relative", p: 1 }}>
								{/* Action buttons overlay */}
								<Stack
									className="admin-actions"
									direction="row"
									spacing={0.5}
									sx={{
										position: "absolute",
										top: 8,
										right: 8,
										zIndex: 9,
										opacity: 0,
										transition: (theme) => theme.transitions.create("opacity", {
											easing: theme.transitions.easing.easeInOut,
											duration: theme.transitions.duration.shorter,
										}),
									}}
								>
									{viewMode === "trashed" ? (
										<IconButton
											size="small"
											onClick={() => handleRestoreProduct(product)}
											sx={{ bgcolor: "rgba(255,255,255,0.9)", color: "#6bc637", "&:hover": { bgcolor: "white" } }}
										>
											<RestoreIcon fontSize="small" />
										</IconButton>
									) : (
										<>
											<IconButton
												size="small"
												onClick={() => handleOpenEditDialog(product)}
												sx={{ bgcolor: "rgba(255,255,255,0.9)", color: "#6bc637", "&:hover": { bgcolor: "white" } }}
											>
												<EditIcon fontSize="small" />
											</IconButton>
											<IconButton
												size="small"
												onClick={() => handleTrashProduct(product)}
												sx={{ bgcolor: "rgba(255,255,255,0.9)", color: "error.main", "&:hover": { bgcolor: "white" } }}
											>
												<DeleteIcon fontSize="small" />
											</IconButton>
										</>
									)}
								</Stack>
								{product.image ? (
									<Image
										alt={product.title}
										src={product.image}
										ratio="1/1"
										enableEffect={false}
										sx={{ borderRadius: 1.5 }}
										imageSx={{ objectFit: "contain" }}
									/>
								) : (
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											aspectRatio: "1/1",
											borderRadius: 1.5,
											backgroundColor: "grey.100",
											color: "grey.500",
										}}
									>
										<ImageIcon sx={{ fontSize: 48 }} />
									</Box>
								)}
							</Box>
							{/* Info */}
							<Stack spacing={{ xs: 1, sm: 2.5 }} sx={{ p: { xs: 1.5, sm: 3 }, pt: { xs: 1, sm: 2 } }}>
								<Typography
									color="inherit"
									variant="subtitle1"
									sx={{ fontSize: { xs: "0.875rem", sm: "1rem" }, lineHeight: { xs: 1.2, sm: 1.5 } }}
								>
									{product.title}
								</Typography>
								{Number(product.price) > 0 && (
									<Stack direction="row" alignItems="center" sx={{ typography: { xs: "body2", sm: "subtitle1" } }}>
										<Box component="span">{`$ ${product.price}`}</Box>
									</Stack>
								)}
								{product.category_names && product.category_names.length > 0 && (
									<Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
										{product.category_names.map((name, i) => (
											<Chip key={i} label={name} size="small" sx={{ fontSize: "0.65rem", height: 18 }} />
										))}
									</Stack>
								)}
							</Stack>
						</Card>
					))}
				</Box>
			)}

			{/* Create/Edit Dialog */}
			<Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
				<DialogTitle sx={{ fontWeight: 700 }}>{editingProduct ? "Edit Product" : "Create Product"}</DialogTitle>
				<DialogContent>
					<Stack spacing={2.5} sx={{ mt: 1 }}>
						<TextField
							label="Product Title"
							fullWidth
							value={formData.title}
							onChange={(e) => setFormData({ ...formData, title: e.target.value })}
							inputProps={{ maxLength: 200 }}
						/>
						<TextField
							label="Price"
							fullWidth
							type="number"
							value={formData.price}
							onChange={(e) => setFormData({ ...formData, price: e.target.value })}
							InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
						/>
						<FormControl fullWidth>
							<InputLabel>Categories</InputLabel>
							<Select
								multiple
								value={formData.category_ids}
								onChange={(e) => setFormData({ ...formData, category_ids: e.target.value })}
								label="Categories"
								renderValue={(selected) =>
									selected.map((id) => {
										const cat = categoriesAsList.find((c) => c.id === id);
										if (!cat) return id;
										return cat.is_physical ? cat.title : `${cat.title} (Service)`;
									}).join(", ")
								}
							>
								{categoriesAsList.map((cat) => (
									<MenuItem key={cat.id} value={cat.id} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
										{cat.title}
										{!cat.is_physical && (
											<Chip label="Service" size="small" color="warning" sx={{ fontSize: "0.7rem", height: 20, ml: 1 }} />
										)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{/* Image Upload */}
						<Box>
							<Typography variant="body2" color="text.secondary" mb={1}>Product Image</Typography>
							<Button variant="outlined" component="label" sx={{ borderRadius: "12px", textTransform: "none" }}>
								Choose Image
								<input type="file" accept="image/*" hidden onChange={handleImageChange} />
							</Button>
							{imagePreview && (
								<Box sx={{ mt: 1.5, width: 120, height: 120, borderRadius: "12px", overflow: "hidden", border: "1px solid #e0e0e0" }}>
									<img src={imagePreview} alt="Preview" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
								</Box>
							)}
						</Box>
					</Stack>
				</DialogContent>
				<DialogActions sx={{ p: 2, pt: 0 }}>
					<Button onClick={handleCloseDialog} sx={{ borderRadius: "12px", textTransform: "none", color: "grey.600" }}>Cancel</Button>
					<Button
						onClick={handleSubmit}
						variant="contained"
						disabled={isLoading}
						sx={{ borderRadius: "12px", bgcolor: "#6bc637", "&:hover": { bgcolor: "#5ab02e" }, textTransform: "none", fontWeight: 600 }}
					>
						{isLoading ? <CircularProgress size={24} /> : editingProduct ? "Update" : "Create"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);

	return content;
}

export default AdminProductsPage;
